import { forwardRef } from 'react';
import { Button, ButtonProps } from '@mui/material';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  useInRouterContext,
} from 'react-router-dom';

/**
 * A call to action: either it goes somewhere (`href`) or it does something
 * (`onClick`, e.g. Undo or opening a modal).
 */
export type ActionProps = ButtonProps & { label: string };

/** A react-router Link that takes `href`, for MUI's `LinkComponent`. */
const RouterHrefLink = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: string }
>(({ href, ...props }, ref) => <RouterLink ref={ref} to={href} {...props} />);

/**
 * A MUI Button that, given an `href`, renders as a real link — an in-app path
 * routes through react-router (no reload), anything else is a plain anchor —
 * so cmd/ctrl/middle-click opens it in a new tab. Without one it's a button.
 */
export function LinkAwareButton({ href, ...props }: ButtonProps) {
  const inRouter = useInRouterContext();
  return (
    <Button
      {...props}
      href={href}
      LinkComponent={
        href && inRouter && href.startsWith('/') ? RouterHrefLink : undefined
      }
    />
  );
}
